const DEFAULT_COMPLETE_PATH = '/mcp/oauth/authorize/complete';
const ALLOWED_COMPLETE_PATHS = new Set([
  DEFAULT_COMPLETE_PATH,
  '/mcp/prd/oauth/authorize/complete',
]);

const SESSION_STORAGE_KEY = 'eliza:mcp_oauth_session';
const COMPLETE_PATH_STORAGE_KEY = 'eliza:mcp_oauth_complete_path';

export interface PendingMcpOAuth {
  session: string;
  completePath: string;
}

function getApiBaseUrl(): string {
  return process.env.REACT_APP_API_URL || '';
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function normalizeMcpOAuthCompletePath(value?: string | null): string {
  const candidate = (value || DEFAULT_COMPLETE_PATH).trim();
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.includes('\\')) {
    throw new Error('Invalid OAuth completion path.');
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate, 'https://eliza.local');
  } catch {
    throw new Error('Invalid OAuth completion path.');
  }

  if (
    parsed.origin !== 'https://eliza.local' ||
    parsed.search ||
    parsed.hash
  ) {
    throw new Error('Invalid OAuth completion path.');
  }

  const normalized = parsed.pathname.replace(/\/+$/, '') || '/';
  if (!ALLOWED_COMPLETE_PATHS.has(normalized)) {
    throw new Error('Invalid OAuth completion path.');
  }
  return normalized;
}

export function getMcpOAuthRequestFromSearchParams(
  searchParams: URLSearchParams,
): PendingMcpOAuth | null {
  const session = searchParams.get('mcp_oauth_session')?.trim();
  if (!session) return null;
  return {
    session,
    completePath: normalizeMcpOAuthCompletePath(
      searchParams.get('mcp_oauth_complete_path'),
    ),
  };
}

export function storePendingMcpOAuth(pending: PendingMcpOAuth): void {
  const sessionStorage = storage();
  if (!sessionStorage) return;
  sessionStorage.setItem(SESSION_STORAGE_KEY, pending.session);
  sessionStorage.setItem(
    COMPLETE_PATH_STORAGE_KEY,
    normalizeMcpOAuthCompletePath(pending.completePath),
  );
}

export function getPendingMcpOAuth(): PendingMcpOAuth | null {
  const sessionStorage = storage();
  if (!sessionStorage) return null;

  const session = sessionStorage.getItem(SESSION_STORAGE_KEY)?.trim();
  if (!session) return null;

  try {
    return {
      session,
      completePath: normalizeMcpOAuthCompletePath(
        sessionStorage.getItem(COMPLETE_PATH_STORAGE_KEY),
      ),
    };
  } catch {
    clearPendingMcpOAuth();
    return null;
  }
}

export function clearPendingMcpOAuth(): void {
  const sessionStorage = storage();
  if (!sessionStorage) return;
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  sessionStorage.removeItem(COMPLETE_PATH_STORAGE_KEY);
}

export async function completeMcpOAuthFlow(
  pending: PendingMcpOAuth,
  accessToken: string,
): Promise<string> {
  const endpoint = `${getApiBaseUrl()}${normalizeMcpOAuthCompletePath(pending.completePath)}`;
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ session: pending.session }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error_description || 'Failed to complete MCP authorization.');
  }
  if (!data || typeof data.redirect_url !== 'string' || !data.redirect_url) {
    throw new Error('MCP authorization did not return a redirect URL.');
  }
  return data.redirect_url;
}
